import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Avatar, AvatarBadge, AvatarFallback } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuthContext } from '../../common/hooks/use-auth-context.hook';
import { displayNameFor, initialsFor } from '../../common/utils/user-display';
import { PresenceMember, selectOthers, usePresenceStore } from './presence.store';
import { pageLabel, statusLabel } from './presence-format';

/** Faces in the bar before the rest fold into "+N". */
const MAX_FACES = 3;

/** Re-renders every half minute so "away · 4 min" keeps counting. */
function useNow(everyMs = 30_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}

function PresenceFace({ member, size = 'sm', className }: { member: PresenceMember; size?: 'sm' | 'default'; className?: string }) {
  const away = member.status === 'away';
  return (
    <Avatar size={size} className={cn(size === 'sm' && 'size-7', className)}>
      <AvatarFallback
        className={cn(
          'bg-muted font-medium text-foreground transition-opacity',
          size === 'sm' ? 'text-[11px]' : 'text-xs',
          away && 'opacity-50',
        )}
      >
        {initialsFor(member.name, member.email, '?')}
      </AvatarFallback>
      {/* Green is the one colour here, and it means one thing: at the keyboard now. */}
      {!away && <AvatarBadge className="bg-emerald-500" />}
    </Avatar>
  );
}

/**
 * Who else is in this account right now — Figma's avatar stack, in the top bar.
 *
 * Hover a face for who it is, where they are and whether they are at the
 * keyboard. Click the stack for the whole list; a row takes you to the page
 * that person is on. Nothing renders when you are alone: an empty stack says
 * nothing a person needs to know.
 */
export function PresenceStack() {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isSuperAdmin } = useAuthContext();
  const others = usePresenceStore(selectOthers);
  const selfUserId = usePresenceStore((s) => s.selfUserId);
  const [open, setOpen] = useState(false);
  const now = useNow();

  if (!others.length) return null;

  const faces = others.slice(0, MAX_FACES);
  const rest = others.length - faces.length;

  // The same login on a second device: a shared account, or you on your phone.
  const isSameAccount = (m: PresenceMember) => !!m.userId && m.userId === selfUserId;
  const nameOf = (m: PresenceMember) =>
    // Ardealy support in an Act-as session says so: never mistaken for a teammate.
    m.support
      ? (t('PRESENCE.SUPPORT', { name: displayNameFor(m.name, m.email, '—') }) as string)
      : isSameAccount(m) ? (t('PRESENCE.SAME_ACCOUNT') as string) : displayNameFor(m.name, m.email, '—');
  const titleOf = (m: PresenceMember) =>
    isSameAccount(m) ? nameOf(m) : (t('PRESENCE.IS_HERE', { name: nameOf(m) }) as string);

  const whereOf = (m: PresenceMember) => {
    if (m.path && m.path === pathname) return t('PRESENCE.SAME_PAGE') as string;
    const label = pageLabel(m, t, isSuperAdmin);
    return label ? (t('PRESENCE.ON_PAGE', { page: label }) as string) : null;
  };

  const go = (m: PresenceMember) => {
    setOpen(false);
    if (m.path && m.path !== pathname) navigate(m.path);
  };

  // Not on a phone: the bar there is already full with the tools.
  return (
    <div className="hidden items-center sm:flex">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t('PRESENCE.TRIGGER', { count: others.length }) as string}
            className="flex items-center rounded-full p-0.5 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <span className="flex -space-x-1.5">
              {faces.map((m) => (
                <Tooltip key={m.key}>
                  {/* A span, not the Avatar: under React 18 the shadcn Avatar takes no ref. */}
                  <TooltipTrigger asChild>
                    <span className="rounded-full ring-2 ring-background">
                      <PresenceFace member={m} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={6}>
                    <span className="flex flex-col gap-0.5 py-0.5">
                      <span className="font-medium">{titleOf(m)}</span>
                      {whereOf(m) && <span className="text-background/70">{whereOf(m)}</span>}
                      <span className="text-background/70">{statusLabel(m, t, now)}</span>
                    </span>
                  </TooltipContent>
                </Tooltip>
              ))}
              {rest > 0 && (
                <span className="relative flex size-7 items-center justify-center rounded-full bg-muted text-[11px] font-medium text-muted-foreground ring-2 ring-background">
                  +{rest}
                </span>
              )}
            </span>
          </button>
        </PopoverTrigger>

        <PopoverContent align="end" className="w-80 p-0">
          <div className="border-b px-4 py-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium">{t('PRESENCE.TITLE')}</span>
              <span className="text-xs tabular-nums text-muted-foreground">{others.length}</span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">{t('PRESENCE.HINT')}</p>
          </div>
          <ul className="max-h-80 overflow-y-auto py-1">
            {others.map((m) => {
              const where = whereOf(m);
              const here = !m.path || m.path === pathname;
              return (
                <li key={m.key}>
                  <button
                    type="button"
                    onClick={() => go(m)}
                    disabled={here}
                    className="group flex w-full items-center gap-3 px-4 py-2 text-start outline-none hover:bg-accent focus-visible:bg-accent disabled:cursor-default disabled:hover:bg-transparent"
                  >
                    <PresenceFace member={m} size="default" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">{nameOf(m)}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {[where, statusLabel(m, t, now)].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    {!here && (
                      <ArrowUpRight className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 rtl:-scale-x-100" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="mx-1 h-5" />
    </div>
  );
}
